<template>
  <v-container fluid>
    <!-- Terminal-style Layout Display -->
    <div
      v-if="filteredObjects && filteredObjects.length > 0"
      class="terminal-container"
    >
      <v-row>
        <v-col
          v-for="(item, index) in filteredObjects"
          :key="`item-${index}-${editingPath.join('-')}`"
          cols="6"
          class="pa-0"
        >
          <v-card
            class="terminal-button elevation-3"
            :style="getItemStyle(item)"
            @click="handleItemClick(item, index)"
          >
            <v-card-text class="pa-3">
              <!-- Main Layout: Name (60%) + Controls (40%) -->
              <div class="d-flex align-center">
                <!-- Name Section (90%) -->
                <div
                  class="name-section cursor-pointer"
                  @click="handleItemClick(item, index)"
                >
                  <div class="name-content">
                    <div
                      class="font-weight-bold text-truncate"
                      :style="{
                        color:
                          (item.textColour && item.textColour.ColourValue) ||
                          '#ffffff',
                      }"
                    >
                      {{ getItemDisplayName(item, index) }}
                    </div>
                    <div
                      v-if="item.itemType === 'Product' && getItemPrice(item)"
                      class="price-text mt-1"
                      :style="{
                        color:
                          (item.textColour && item.textColour.ColourValue) ||
                          '#ffffff',
                      }"
                    >
                      {{ getItemPrice(item) }}
                    </div>
                  </div>
                </div>

                <!-- Controls Section (40%) -->
                <div class="controls-section d-flex flex-column align-center">
                  <!-- Edit Button -->
                  <v-btn
                    icon
                    small
                    class="mb-1"
                    @click.stop="editItem(item, index)"
                    :style="{
                      color:
                        (item.textColour && item.textColour.ColourValue) ||
                        '#ffffff',
                    }"
                  >
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>

                  <!-- Position Controls -->
                  <div class="d-flex">
                    <v-btn
                      icon
                      x-small
                      @click.stop="moveItem(index, index - 1)"
                      :disabled="index === 0"
                      :style="{
                        color:
                          (item.textColour && item.textColour.ColourValue) ||
                          '#ffffff',
                      }"
                    >
                      <v-icon x-small>mdi-arrow-up</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      x-small
                      @click.stop="moveItem(index, index + 1)"
                      :disabled="index === filteredObjects.length - 1"
                      :style="{
                        color:
                          (item.textColour && item.textColour.ColourValue) ||
                          '#ffffff',
                      }"
                    >
                      <v-icon x-small>mdi-arrow-down</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Add Item Button -->
        <v-col cols="6" class="pa-0">
          <v-card
            class="terminal-button add-button elevation-2 d-flex"
            @click="addItem"
          >
            <v-card-text
              class="pa-3 d-flex align-center justify-center flex-grow-1"
            >
              <v-icon size="48" color="white" class="mr-2"
                >mdi-plus-circle-outline</v-icon
              >
              <div class="font-weight-bold white--text">Add Item</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center pa-8">
      <v-icon size="64" color="grey lighten-1">mdi-monitor</v-icon>
      <p class="grey--text mt-4 text-h6">Layout is Empty</p>
      <p class="grey--text">Add your first item to get started</p>
      <v-btn color="primary" large class="mt-4" @click="addItem">
        <v-icon left>mdi-plus</v-icon>
        Add First Item
      </v-btn>
    </div>

    <!-- Item Dialog -->
    <LayoutItemDialog
      v-model="showDialog"
      :item="selectedItem"
      :is-editing="isEditingItem"
      :current-level="currentLevel"
      :is-inside-selection-group="isInsideSelectionGroup"
      @save="saveItem"
      @delete="deleteSelectedItem"
    />
  </v-container>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import LayoutItemDialog from "./LayoutItemDialog.vue";
import { isGroupItem, isSelectionGroupItem } from "./layoutItemUtils";

export default {
  name: "LayoutEditor",

  components: {
    LayoutItemDialog,
  },

  data() {
    return {
      showDialog: false,
      selectedItem: {},
      selectedIndex: -1,
      isEditingItem: false,
      // Snapshot of layout item before edit — restored if dialog is cancelled
      itemSnapshot: null,
      itemSaved: false,
    };
  },

  computed: {
    ...mapState("admin", ["currentLayout", "editingPath", "availableProducts"]),

    currentObjects() {
      let objects = this.currentLayout.Objects;

      // Navigate to the current editing level
      for (let i = 0; i < this.editingPath.length; i++) {
        const index = this.editingPath[i];
        if (objects && objects[index]) {
          if (!objects[index].Objects) {
            objects[index].Objects = [];
          }
          objects = objects[index].Objects;
        } else {
          return [];
        }
      }

      return objects || [];
    },

    filteredObjects() {
      // Show items that have at least itemType set - this allows partially configured items to display
      return this.currentObjects.filter(
        (item) => item.itemType // Much simpler - just need itemType to show
      );
    },

    currentLevel() {
      return this.editingPath.length;
    },

    isInsideSelectionGroup() {
      // Check if we're inside a Selection group by traversing the editing path
      let objects = this.currentLayout.Objects;

      for (let i = 0; i < this.editingPath.length; i++) {
        const index = this.editingPath[i];
        if (objects && objects[index]) {
          const item = objects[index];
          // If this item is a Selection group, we're inside one
          if (isSelectionGroupItem(item)) {
            return true;
          }
          // Move to the next level
          if (item.Objects) {
            objects = item.Objects;
          }
        }
      }

      return false;
    },
  },

  watch: {
    showDialog(isOpen, wasOpen) {
      // If dialog closed without save/delete, restore pre-edit snapshot
      if (
        wasOpen &&
        !isOpen &&
        this.isEditingItem &&
        !this.itemSaved &&
        this.itemSnapshot &&
        this.selectedIndex >= 0
      ) {
        this.updateLayoutItem({
          path: [...this.editingPath, this.selectedIndex],
          item: this.cloneItem(this.itemSnapshot),
        });
      }
      if (!isOpen) {
        this.itemSnapshot = null;
        this.itemSaved = false;
      }
    },
  },

  methods: {
    ...mapMutations("admin", [
      "navigateDown",
      "updateLayoutItem",
      "addLayoutItem",
      "removeLayoutItem",
      "moveLayoutItem",
    ]),

    cloneItem(item) {
      if (!item || typeof item !== "object") {
        return {};
      }
      try {
        return JSON.parse(JSON.stringify(item));
      } catch (e) {
        return { ...item };
      }
    },

    getItemStyle(item) {
      return {
        backgroundColor:
          (item.backgroundColour && item.backgroundColour.ColourValue) ||
          "#333333",
        border: `2px solid ${
          (item.textColour && item.textColour.ColourValue) || "#ffffff"
        }`,
        borderRadius: "8px",
        minHeight: "100px",
        cursor: "pointer",
        transition: "all 0.2s ease",
      };
    },

    getItemDisplayName(item, index) {
      if (isGroupItem(item)) {
        return item.GroupName || `Group ${index + 1}`;
      } else if (item.itemType === "Product") {
        // Handle ProductId format
        if (item.ProductId?.ProductId) {
          const product = this.availableProducts.find(
            (p) => p.ProductId === item.ProductId.ProductId
          );
          return (
            product?.ShortName || product?.ProductName || `Product ${index + 1}`
          );
        }
        // Fallback for old ProductId format (migration support)
        if (item.ProductId) {
          return (
            item.ProductId.ShortName ||
            item.ProductId.ProductName ||
            `Product ${index + 1}`
          );
        }
        return `Product ${index + 1}`;
      }
      return `Item ${index + 1}`;
    },

    getItemPrice(item) {
      if (item.itemType === "Product") {
        // Handle ProductId format
        if (item.ProductId?.ProductId) {
          const product = this.availableProducts.find(
            (p) => p.ProductId === item.ProductId.ProductId
          );
          if (product?.Price) {
            return `$${parseFloat(product.Price).toFixed(2)}`;
          }
        }
        // Fallback for old ProductId format (migration support)
        if (item.ProductId?.Price) {
          return `$${parseFloat(item.ProductId.Price).toFixed(2)}`;
        }
      }
      return "";
    },

    handleItemClick(item, index) {
      // If it's a folder/group icon click and can have nested items
      if (isGroupItem(item) && this.currentLevel < 3) {
        this.navigateToItem(index);
      }
    },

    navigateToItem(index) {
      // Find the actual index in the full array (including empty items)
      const actualIndex = this.findActualIndex(index);
      const item = this.currentObjects[actualIndex];

      if (isGroupItem(item) && this.currentLevel < 3) {
        // Initialize Objects array if it doesn't exist
        if (!item.Objects) {
          const itemPath = [...this.editingPath, actualIndex];
          this.updateLayoutItem({
            path: itemPath,
            item: { ...item, Objects: [] }, // Start with empty array instead of one empty object
          });
        }
        this.navigateDown(actualIndex);
        // Force update to ensure reactivity after navigation
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      }
    },

    findActualIndex(filteredIndex) {
      // Align with filteredObjects (itemType) so Selection groups missing
      // GroupName (pre-fix corruption) can still be edited/moved.
      let count = 0;
      for (let i = 0; i < this.currentObjects.length; i++) {
        const item = this.currentObjects[i];
        if (item.itemType) {
          if (count === filteredIndex) {
            return i;
          }
          count++;
        }
      }
      return filteredIndex; // fallback
    },

    editItem(item, index) {
      this.selectedIndex = this.findActualIndex(index);
      // Deep clone so dialog cannot mutate live layout state via shared refs
      this.itemSnapshot = this.cloneItem(item);
      this.selectedItem = this.cloneItem(item);
      this.itemSaved = false;
      this.isEditingItem = true;
      this.showDialog = true;
    },

    addItem() {
      this.selectedIndex = -1;
      this.selectedItem = {};
      this.itemSnapshot = null;
      this.itemSaved = false;
      this.isEditingItem = false;
      this.showDialog = true;
    },

    saveItem(item) {
      this.itemSaved = true;
      if (this.isEditingItem) {
        // Merge with existing so omitted fields (e.g. GroupName) are never dropped
        const existing = this.currentObjects[this.selectedIndex] || {};
        const itemPath = [...this.editingPath, this.selectedIndex];
        this.updateLayoutItem({
          path: itemPath,
          item: {
            ...existing,
            ...item,
            GroupName:
              item.GroupName != null && item.GroupName !== ""
                ? item.GroupName
                : existing.GroupName,
            SelectionName:
              item.SelectionName != null && item.SelectionName !== ""
                ? item.SelectionName
                : existing.SelectionName,
            GroupType: item.GroupType || existing.GroupType,
            Objects: item.Objects != null ? item.Objects : existing.Objects,
            level: this.currentLevel + 1,
          },
        });
      } else {
        // Add new item - simplified approach
        // Just add the item directly to the current array
        this.addLayoutItem({
          path: this.editingPath,
          item: {
            ...item,
            level: this.currentLevel + 1,
          },
        });
      }

      // Ensure the UI updates after the state change
      this.$nextTick(() => {
        // The reactive mutations should handle the update automatically
      });
    },

    deleteSelectedItem() {
      this.itemSaved = true; // prevent snapshot restore after delete
      if (this.selectedIndex >= 0) {
        this.removeLayoutItem({
          path: this.editingPath,
          index: this.selectedIndex,
        });
      }
    },

    moveItem(fromIndex, toIndex) {
      if (
        toIndex >= 0 &&
        toIndex < this.filteredObjects.length &&
        fromIndex !== toIndex
      ) {
        const actualFromIndex = this.findActualIndex(fromIndex);
        const actualToIndex = this.findActualIndex(toIndex);

        this.moveLayoutItem({
          path: this.editingPath,
          fromIndex: actualFromIndex,
          toIndex: actualToIndex,
        });
      }
    },

    isValidItem(item) {
      if (!item.itemType) return false;

      if (isGroupItem(item)) {
        const hasName = item.GroupName;
        const hasGroupType = item.GroupType;
        const hasSelectionName =
          !isSelectionGroupItem(item) || item.SelectionName;
        const hasColors = item.textColour && item.backgroundColour;

        return hasName && hasGroupType && hasSelectionName && hasColors;
      } else if (item.itemType === "Product") {
        return item.ProductId && item.textColour && item.backgroundColour;
      }

      return false;
    },
  },
};
</script>

<style scoped>
.terminal-container {
  background-color: #1e1e1e;
  border-radius: 8px;
  padding: 16px;
  min-height: 200px;
}

.terminal-button {
  height: 100%;
  min-height: 100px;
  transition: all 0.2s ease;
  cursor: pointer;
  border-radius: 8px !important;
}

.terminal-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3) !important;
}

.add-button {
  background: linear-gradient(145deg, #2c2c2c, #404040) !important;
  border: 2px dashed #666 !important;
  color: #fff !important;
}

.add-button:hover {
  border-color: #1976d2 !important;
  background: linear-gradient(145deg, #303030, #454545) !important;
}

.cursor-pointer {
  cursor: pointer;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Terminal-like font for a retro feel */
.terminal-button .font-weight-bold {
  font-family: "Courier New", monospace;
  letter-spacing: 0.5px;
}

/* Improve button visibility */
.terminal-button .v-btn--icon:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

/* Empty state styling */
.text-center {
  background-color: #1e1e1e;
  border-radius: 8px;
  padding: 32px;
  margin: 16px 0;
}

/* Layout sections */
.name-section {
  width: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.name-content {
  text-align: center;
  width: 100%;
}

.controls-section {
  width: 10%;
  min-height: 60px;
}

/* Price text styling */
.price-text {
  font-size: 0.85em;
  opacity: 0.9;
  font-family: "Courier New", monospace;
}
</style>
